import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../context/AuthContext'
import Layout from '../components/Layout'

export default function TransactionDetail() {
  const { id } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [transaction, setTransaction] = useState(null)
  const [auditLog, setAuditLog] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchTransaction()
  }, [id])

  const fetchTransaction = async () => {
    setLoading(true)

    const { data: tx, error } = await supabase
      .from('transactions')
      .select(`
        *,
        accounts(name, account_type),
        trusts(name, state_of_domicile),
        beneficiaries(full_name),
        categories(name, type)
      `)
      .eq('id', id)
      .single()

    if (error || !tx) {
      console.log('Transaction error:', error)
      setLoading(false)
      return
    }

    // Fetch the profile of who entered the transaction separately
    if (tx.entered_by) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', tx.entered_by)
        .single()
      tx.enteredByProfile = profile
    }

    setTransaction(tx)

    // Fetch audit log for this transaction
    const { data: logs } = await supabase
      .from('audit_log')
      .select('*, profiles(full_name, email)')
      .eq('record_id', id)
      .order('created_at', { ascending: false })

    setAuditLog(logs || [])
    setLoading(false)
  }

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD'
  }).format(amount || 0)

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric'
  })

  const formatDateTime = (dateStr) => new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  })

  const typeColors = {
    income: 'bg-green-100 text-green-700',
    disbursement: 'bg-red-100 text-red-700',
    transfer: 'bg-blue-100 text-blue-700',
    fee: 'bg-orange-100 text-orange-700',
    tax: 'bg-purple-100 text-purple-700',
    corporate_action: 'bg-yellow-100 text-yellow-700',
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400">Loading transaction...</p>
        </div>
      </Layout>
    )
  }

  if (!transaction) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-400">Transaction not found.</p>
          <button
            onClick={() => navigate('/transactions')}
            className="text-blue-600 text-sm mt-2 hover:underline"
          >
            Back to transactions
          </button>
        </div>
      </Layout>
    )
  }

  const isCorporateAction = transaction.type === 'corporate_action'
  const details = transaction.corporate_action_details || {}

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/transactions')}
            className="text-gray-400 hover:text-gray-600 text-sm"
          >
            ← Back
          </button>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">{transaction.description}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {transaction.trusts?.name} · {formatDate(transaction.date)}
            </p>
          </div>
          <span className={`text-xs font-medium px-3 py-1.5 rounded-full capitalize ${typeColors[transaction.type] || 'bg-gray-100 text-gray-600'}`}>
            {transaction.type?.replace('_', ' ')}
          </span>
        </div>

        {/* Amount Summary Card */}
        <div className="bg-blue-600 rounded-xl p-6 text-white">
          <p className="text-blue-200 text-sm mb-1">Total Amount</p>
          <p className="text-4xl font-bold mb-4">
            {transaction.type === 'income' ? '+' : '-'}{formatCurrency(transaction.amount)}
          </p>
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-blue-500">
            <div>
              <p className="text-blue-200 text-xs">Principal</p>
              <p className="text-white font-semibold text-lg mt-0.5">
                {formatCurrency(transaction.principal_amount)}
              </p>
            </div>
            <div>
              <p className="text-blue-200 text-xs">Income</p>
              <p className="text-white font-semibold text-lg mt-0.5">
                {formatCurrency(transaction.income_amount)}
              </p>
            </div>
          </div>
        </div>

        {/* Transaction Details */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Transaction Details</h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">

            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Date</p>
              <p className="text-sm font-medium text-gray-800 mt-1">{formatDate(transaction.date)}</p>
            </div>

            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Account</p>
              <p className="text-sm font-medium text-gray-800 mt-1">
                {transaction.accounts?.name || '—'}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Trust</p>
              <p className="text-sm font-medium text-gray-800 mt-1">{transaction.trusts?.name || '—'}</p>
            </div>

            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Category</p>
              <p className="text-sm font-medium text-gray-800 mt-1">{transaction.categories?.name || '—'}</p>
            </div>

            {transaction.beneficiaries && (
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Beneficiary</p>
                <p className="text-sm font-medium text-gray-800 mt-1">
                  {transaction.beneficiaries?.full_name || '—'}
                </p>
              </div>
            )}

            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Reconciled</p>
              <p className="mt-1">
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                  transaction.reconciled
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {transaction.reconciled ? '✓ Reconciled' : 'Not reconciled'}
                </span>
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Entered By</p>
              <p className="text-sm font-medium text-gray-800 mt-1">
                {transaction.enteredByProfile?.full_name || transaction.enteredByProfile?.email || '—'}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Created</p>
              <p className="text-sm font-medium text-gray-800 mt-1">
                {formatDateTime(transaction.created_at)}
              </p>
            </div>

          </div>

          {/* Allocation Basis */}
          {transaction.allocation_basis && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                Allocation Basis
              </p>
              <p className="text-sm text-gray-700">{transaction.allocation_basis}</p>
            </div>
          )}
        </div>

        {/* Corporate Action Details */}
        {isCorporateAction && transaction.corporate_action_type && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-800 mb-4">
              Corporate Action —{' '}
              <span className="capitalize font-normal text-gray-600">
                {transaction.corporate_action_type?.replace('_', ' ')}
              </span>
            </h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              {Object.entries(details).map(([key, value]) => (
                <div key={key}>
                  <p className="text-xs text-gray-400 uppercase tracking-wide">
                    {key.replace(/_/g, ' ')}
                  </p>
                  <p className="text-sm font-medium text-gray-800 mt-1">{value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Audit Trail */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Audit Trail</h3>
          {auditLog.length === 0 ? (
            <p className="text-sm text-gray-400">No audit records found.</p>
          ) : (
            <div className="space-y-3">
              {auditLog.map((log) => (
                <div key={log.id} className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
                  <div className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-800">
                      <span className="font-medium">
                        {log.profiles?.full_name || log.profiles?.email || 'System'}
                      </span>
                      {' '}{log.action?.replace(/_/g, ' ')}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatDateTime(log.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate(`/transactions/${id}/edit`)}
            className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium py-2.5 rounded-lg transition text-sm"
          >
            Edit Transaction
          </button>
          <button
            onClick={() => navigate('/transactions')}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition text-sm"
          >
            Back to Ledger
          </button>
        </div>

      </div>
    </Layout>
  )
}